'use client'

import { useState, useRef, useCallback } from 'react'
import { Button } from '@/ui/button'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/ui/alert-dialog'
import { UploadSimpleIcon } from '@phosphor-icons/react'
import { SpinnerGapIcon } from '@phosphor-icons/react'
import {
  useDocumentOps,
  useDocumentView,
  useDocumentStatus,
} from './use-document'
import {
  validateBackup,
  mergeBackup,
  parseBackupPreview,
  type BackupFile,
  type BackupCounts,
  type MergeResult,
} from './backup'
import type { AppDocument } from './schema'
import type { Entry, Topic, Area, Project, Link } from './schema'

type ImportState = 'idle' | 'previewing' | 'importing' | 'success' | 'failed'

// All seven keyed members, so a restore brings back the trust map and share registry that a
// backup written before #93 could not carry
const IMPORT_KEYS = [
  'entries',
  'topics',
  'areas',
  'projects',
  'links',
  'trust',
  'sharedEntries',
] as const

function formatCounts(counts: BackupCounts): string {
  const parts: string[] = []
  if (counts.entries > 0) parts.push(`${String(counts.entries)} entries`)
  if (counts.topics > 0) parts.push(`${String(counts.topics)} topics`)
  if (counts.areas > 0) parts.push(`${String(counts.areas)} areas`)
  if (counts.projects > 0) parts.push(`${String(counts.projects)} projects`)
  if (counts.links > 0) parts.push(`${String(counts.links)} links`)
  // Absent, not zero, on a backup written before #93
  if ((counts.trust ?? 0) > 0)
    parts.push(`${String(counts.trust)} trusted identities`)
  if ((counts.sharedEntries ?? 0) > 0)
    parts.push(`${String(counts.sharedEntries)} shared entries`)
  return parts.length > 0 ? parts.join(', ') : 'nothing'
}

export function ImportDataButton() {
  const doc = useDocumentView()
  const ready = useDocumentStatus()
  const ops = useDocumentOps()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [state, setState] = useState<ImportState>('idle')
  const [error, setError] = useState<string>('')
  const [preview, setPreview] = useState<BackupCounts | null>(null)
  const [backup, setBackup] = useState<BackupFile | null>(null)
  const [result, setResult] = useState<MergeResult | null>(null)

  const resetState = useCallback(() => {
    setState('idle')
    setError('')
    setPreview(null)
    setBackup(null)
    setResult(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }, [])

  const handleFileSelect = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0]
      if (!file) return

      const reader = new FileReader()
      reader.onload = (e) => {
        try {
          const parsed: unknown = JSON.parse(e.target?.result as string)
          const validation = validateBackup(parsed)

          if (!validation.valid) {
            setState('failed')
            setError(validation.error)
            return
          }

          const counts = parseBackupPreview(validation.backup)
          setBackup(validation.backup)
          setPreview(counts)
          setState('previewing')
        } catch {
          setState('failed')
          setError('Invalid JSON file')
        }
      }
      reader.onerror = () => {
        setState('failed')
        setError('Failed to read file')
      }
      reader.readAsText(file)
    },
    [],
  )

  const handleConfirmImport = useCallback(() => {
    if (!doc || !backup) return

    setState('importing')

    try {
      const mergeResult = mergeBackup(doc, backup)

      for (const key of IMPORT_KEYS) {
        const toImportMap = mergeResult.toImport[key] ?? {}
        if (Object.keys(toImportMap).length === 0) continue

        // One container per change, which is also what keeps a large restore under the
        // single-transaction size ceiling a whole-document write would hit (#367, F23)
        ops.importEntities(key, toImportMap)
      }

      setResult(mergeResult)
      setState('success')
    } catch (err) {
      setState('failed')
      setError(err instanceof Error ? err.message : 'Import failed')
    }
  }, [doc, backup, ops])

  if (!ready) {
    return (
      <Button disabled variant="outline" size="lg" className="flex-1">
        <SpinnerGapIcon className="mr-2 h-4 w-4 animate-spin" />
        Loading...
      </Button>
    )
  }

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept=".json"
        className="hidden"
        onChange={handleFileSelect}
        aria-label="Select backup file"
      />

      {state === 'idle' && (
        <Button
          onClick={() => fileInputRef.current?.click()}
          variant="outline"
          size="lg"
          className="flex-1"
          aria-label="Import data"
        >
          <UploadSimpleIcon className="mr-2 h-4 w-4" />
          Import Data
        </Button>
      )}

      {state === 'importing' && (
        <Button disabled variant="outline" size="lg" className="flex-1">
          <SpinnerGapIcon className="mr-2 h-4 w-4 animate-spin" />
          Importing...
        </Button>
      )}

      {state === 'success' && result && (
        <div className="flex flex-1 flex-col gap-2">
          <div className="text-sm">
            <p className="font-medium">Import complete</p>
            <p className="text-muted-foreground">
              Imported {formatCounts(result.imported)}.
              {formatCounts(result.skipped) !== 'nothing' &&
                ` Skipped ${formatCounts(result.skipped)}.`}
            </p>
          </div>
          <Button onClick={resetState} variant="outline" size="sm">
            Done
          </Button>
        </div>
      )}

      {state === 'failed' && (
        <div className="flex flex-1 flex-col gap-2">
          <p className="text-destructive text-sm">{error}</p>
          <Button onClick={resetState} variant="outline" size="sm">
            Try Again
          </Button>
        </div>
      )}

      <AlertDialog
        open={state === 'previewing'}
        onOpenChange={(open) => {
          if (!open) resetState()
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Import Backup</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-4">
                <p>
                  This backup contains{' '}
                  {formatCounts(
                    preview ?? {
                      entries: 0,
                      topics: 0,
                      areas: 0,
                      projects: 0,
                      links: 0,
                    },
                  )}
                  .
                </p>
                <p className="text-muted-foreground text-sm">
                  Existing entries will not be overwritten.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmImport}>
              Import
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
